<template>
  <v-row>
    <!-- Header Buttons -->
    <v-col cols="12" md="6" class="text-left pb-0 mb-0">
      <v-btn
        small
        elevation="2"
        class="text-caption font-weight-medium"
        color="blue darken-1 white--text"
        @click="statusFilter = 'pending'; fetchPayments()"
      >
        <font-awesome-icon icon="fa-solid fa-clock" class="me-2" />
        Pending Payments
      </v-btn>
    </v-col>
    <v-col cols="12" md="6" class="text-right pb-0 mb-0">
      <v-btn
        small
        elevation="2"
        class="text-caption font-weight-medium"
        color="blue darken-2 white--text"
        @click="statusFilter = 'confirmed'; fetchPayments()"
      >
        <font-awesome-icon icon="fa-solid fa-check-circle" class="me-2" />
        Validated Payments
      </v-btn>
    </v-col>

    <!-- Main Card -->
    <v-col cols="12">
      <v-card class="elevation-3">
        <!-- Filters Row -->
        <v-row class="ma-2 pt-4">
          <v-col cols="12" md="4" class="py-1">
            <v-select
              v-model="statusFilter"
              outlined
              dense
              :items="statusOptions"
              label="Status Filter"
              prepend-inner-icon="mdi-filter"
              background-color="white"
              @change="fetchPayments"
            ></v-select>
          </v-col>

          <v-col cols="12" md="4" class="py-1">
            <v-select
              v-model="operatorFilter"
              outlined
              dense
              :items="operatorOptions"
              label="Operator Filter"
              prepend-inner-icon="mdi-phone"
              background-color="white"
              @change="fetchPayments"
            ></v-select>
          </v-col>

          <v-col cols="10" md="3" class="py-1">
            <v-text-field
              v-model="search"
              outlined
              dense
              label="Search payments..."
              prepend-inner-icon="mdi-magnify"
              clearable
              background-color="white"
            ></v-text-field>
          </v-col>

          <v-col cols="2" md="1" class="text-right py-1">
            <spinner v-if="loading" class="text-right"></spinner>
            <v-btn
              v-else
              icon
              small
              color="blue"
              class="elevation-2"
              @click="fetchPayments()"
            >
              <font-awesome-icon icon="fa-solid fa-sync" />
            </v-btn>
          </v-col>
        </v-row>

        <!-- Statistics Cards -->
        <v-row class="ma-2">
          <v-col cols="12" md="4">
            <v-card color="blue lighten-5" elevation="2" class="stat-card">
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption font-weight-medium blue--text text--darken-4">Pending Payments</div>
                    <div class="text-h5 font-weight-bold blue--text text--darken-4">{{ stats.pending }}</div>
                  </div>
                  <v-icon size="36" color="blue darken-2">mdi-clock-outline</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card color="blue lighten-4" elevation="2" class="stat-card">
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption font-weight-medium blue--text text--darken-4">Validated Payments</div>
                    <div class="text-h5 font-weight-bold blue--text text--darken-4">{{ stats.validated }}</div>
                  </div>
                  <v-icon size="36" color="blue darken-2">mdi-check-circle</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card color="blue lighten-3" elevation="2" class="stat-card">
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption font-weight-medium blue--text text--darken-4">Total Amount (Pending)</div>
                    <div class="text-h6 font-weight-bold blue--text text--darken-4">{{ formatAmount(stats.totalAmount) }}</div>
                  </div>
                  <v-icon size="36" color="blue darken-2">mdi-cash-multiple</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Table Header -->
        <v-card-title class="pt-4 pb-2">
          <div class="d-flex align-center">
            <v-icon color="blue" class="me-2">mdi-format-list-bulleted</v-icon>
            <span class="text-h6 font-weight-medium blue--text text--darken-2">Payment Requests</span>
            <v-chip class="ms-3" color="blue" dark small>
              {{ statusFilter.toUpperCase() }}
            </v-chip>
          </div>
          <v-spacer></v-spacer>
          <span class="text-body-1 grey--text text--darken-3 font-weight-medium">
            ({{ filteredPayments.length }}) records
          </span>
        </v-card-title>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="filteredPayments"
          item-key="id"
          class="table-rounded elevation-1"
          :items-per-page="15"
          disable-sort
          :loading="loading"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50],
            'items-per-page-text': 'Rows per page:'
          }"
        >
          <!-- Customer Info -->
          <template #[`item.user`]="{item}">
            <div class="d-flex flex-column" style="cursor: pointer">
              <span class="d-block font-weight-semibold text--primary text-body-2">{{ item.user.username }}</span>
              <small class="text-caption grey--text text--darken-2">{{ item.user.email }}</small>
            </div>
          </template>

          <!-- Order Info -->
          <template #[`item.order`]="{item}">
            <div class="d-flex flex-column">
              <span class="font-weight-semibold text-body-2">{{ item.order.order_id }}</span>
              <small class="text-caption grey--text text--darken-2">{{ item.order.shipping_location }}</small>
            </div>
          </template>

          <!-- Amount -->
          <template #[`item.amount`]="{item}">
            <span class="font-weight-bold blue--text text--darken-1 text-body-2">{{ formatAmount(item.amount) }}</span>
          </template>

          <!-- Payment Info -->
          <template #[`item.payment_info`]="{item}">
            <div class="d-flex flex-column">
              <small class="text-caption"><strong class="grey--text text--darken-3">Ref:</strong> {{ item.payment_ref }}</small>
              <small class="text-caption"><strong class="grey--text text--darken-3">Phone:</strong> {{ item.phone_number }}</small>
              <v-chip x-small color="blue" dark class="mt-1">
                {{ item.payment_operator }}
              </v-chip>
            </div>
          </template>

          <!-- Status with Click Action -->
          <template #[`item.confirmed`]="{item}">
            <v-chip
              small
              :color="statusColor[item.confirmed]"
              class="font-weight-medium white--text"
              @click="openStatusChangeDialog(item)"
              style="cursor: pointer"
            >
              <v-icon left small>{{ item.confirmed ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
              <span v-if="item.confirmed">Validated</span>
              <span v-else>Pending</span>
            </v-chip>
          </template>

          <!-- Date -->
          <template #[`item.creation_date_str`]="{item}">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="text-caption grey--text text--darken-3">
                  {{ formatDateShort(item.creation_date) }}
                </span>
              </template>
              <span>{{ item.creation_date_str }}</span>
            </v-tooltip>
          </template>

          <!-- Actions -->
          <template #[`item.actions`]="{item}">
            <v-btn
              icon
              small
              color="blue"
              @click="viewDetails(item)"
            >
              <font-awesome-icon icon="fa-solid fa-eye" />
            </v-btn>
          </template>

          <!-- Empty State -->
          <template v-slot:no-data>
            <div class="text-center pa-5">
              <v-icon size="64" color="grey lighten-1">mdi-inbox-outline</v-icon>
              <div class="text-h6 grey--text text--darken-2 mt-3">No payment requests found</div>
              <v-btn color="blue" class="mt-3" @click="fetchPayments" small>
                <font-awesome-icon icon="fa-solid fa-refresh" class="me-2" />
                Refresh
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- Status Change Dialog -->
    <v-dialog v-model="statusDialog" max-width="500">
      <v-card>
        <v-card-title
          class="white--text"
          :class="selectedPayment && selectedPayment.confirmed ? 'blue darken-2' : 'blue darken-1'"
        >
          <v-icon left color="white">
            {{ selectedPayment && selectedPayment.confirmed ? 'mdi-close-circle' : 'mdi-check-circle' }}
          </v-icon>
          {{ selectedPayment && selectedPayment.confirmed ? 'Change to Pending?' : 'Validate Payment?' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <div v-if="selectedPayment">
            <p class="text-body-1 font-weight-medium grey--text text--darken-4">
              {{ selectedPayment.confirmed
                ? 'Are you sure you want to mark this payment as PENDING?'
                : 'Are you sure you want to VALIDATE this payment?'
              }}
            </p>

            <v-divider class="my-3"></v-divider>

            <div class="payment-summary pa-3">
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Customer:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.user.username }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Order ID:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.order.order_id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Amount:</td>
                    <td class="font-weight-bold blue--text text--darken-2 text-caption">{{ formatAmount(selectedPayment.amount) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Payment Ref:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.payment_ref }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <!-- Cancellation Reason (only if going from validated to pending) -->
            <div v-if="selectedPayment.confirmed" class="mt-4">
              <v-textarea
                v-model="cancelReason"
                label="Reason for changing to Pending *"
                placeholder="Please provide a reason for this status change..."
                outlined
                rows="3"
                :rules="[v => !!v || 'Reason is required']"
                counter
                dense
                background-color="white"
              ></v-textarea>

              <v-alert type="warning" dense class="mt-2">
                The customer will be notified of this change via email.
              </v-alert>
            </div>

            <v-alert v-else type="info" dense class="mt-3">
              This will mark the payment as validated and confirm the order.
            </v-alert>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="statusDialog = false" :disabled="processing">
            Cancel
          </v-btn>
          <v-btn
            :color="selectedPayment && selectedPayment.confirmed ? 'blue darken-2' : 'blue darken-1'"
            dark
            @click="confirmStatusChange"
            :loading="processing"
            :disabled="selectedPayment && selectedPayment.confirmed && !cancelReason"
          >
            <v-icon left>
              {{ selectedPayment && selectedPayment.confirmed ? 'mdi-close' : 'mdi-check' }}
            </v-icon>
            {{ selectedPayment && selectedPayment.confirmed ? 'Change to Pending' : 'Validate Payment' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="700">
      <v-card v-if="selectedPayment">
        <v-card-title class="text-h5 white--text blue darken-2">
          <v-icon left color="white">mdi-information-outline</v-icon>
          Payment Details
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <h3 class="mb-3 text-subtitle-1 blue--text text--darken-2 font-weight-bold">Payment Information</h3>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Status:</td>
                    <td>
                      <v-chip :color="selectedPayment.confirmed ? 'blue' : 'blue lighten-1'" dark x-small>
                        {{ selectedPayment.confirmed ? 'VALIDATED' : 'PENDING' }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Amount:</td>
                    <td class="text-body-2 blue--text text--darken-2 font-weight-bold">{{ formatAmount(selectedPayment.amount) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Payment Ref:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.payment_ref }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Phone Number:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.phone_number }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Operator:</td>
                    <td>
                      <v-chip x-small color="blue" dark>
                        {{ selectedPayment.payment_operator }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>

            <v-col cols="12" md="6">
              <h3 class="mb-3 text-subtitle-1 blue--text text--darken-2 font-weight-bold">Customer Information</h3>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Username:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.user.username }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Email:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.user.email }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>

            <v-col cols="12">
              <v-divider class="my-3"></v-divider>
              <h3 class="mb-3 text-subtitle-1 blue--text text--darken-2 font-weight-bold">Order Information</h3>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Order ID:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.order.order_id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption grey--text text--darken-4">Shipping Location:</td>
                    <td class="text-caption grey--text text--darken-3">{{ selectedPayment.order.shipping_location }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" text @click="detailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<template>
  <v-row>
    <!-- Header Buttons -->
    <v-col cols="12" md="6" class="text-left pb-0 mb-0">
      <v-btn
        small
        elevation="2"
        class="text-caption font-weight-medium"
        color="blue-grey darken-1"
        @click="statusFilter = 'pending'; fetchPayments()"
      >
        <font-awesome-icon icon="fa-solid fa-clock" class="me-2" />
        Pending Payments
      </v-btn>
    </v-col>
    <v-col cols="12" md="6" class="text-right pb-0 mb-0">
      <v-btn
        small
        elevation="2"
        class="text-caption font-weight-medium"
        color="teal darken-2"
        @click="statusFilter = 'confirmed'; fetchPayments()"
      >
        <font-awesome-icon icon="fa-solid fa-check-circle" class="me-2" />
        Validated Payments
      </v-btn>
    </v-col>

    <!-- Main Card -->
    <v-col cols="12">
      <v-card class="elevation-3">
        <!-- Filters Row -->
        <v-row class="ma-2 pt-4">
          <v-col cols="12" md="4" class="py-1">
            <v-select
              v-model="statusFilter"
              outlined
              dense
              :items="statusOptions"
              label="Status Filter"
              prepend-inner-icon="mdi-filter"
              @change="fetchPayments"
            ></v-select>
          </v-col>

          <v-col cols="12" md="4" class="py-1">
            <v-select
              v-model="operatorFilter"
              outlined
              dense
              :items="operatorOptions"
              label="Operator Filter"
              prepend-inner-icon="mdi-phone"
              @change="fetchPayments"
            ></v-select>
          </v-col>

          <v-col cols="10" md="3" class="py-1">
            <v-text-field
              v-model="search"
              outlined
              dense
              label="Search payments..."
              prepend-inner-icon="mdi-magnify"
              clearable
            ></v-text-field>
          </v-col>

          <v-col cols="2" md="1" class="text-right py-1">
            <spinner v-if="loading" class="text-right"></spinner>
            <v-btn
              v-else
              icon
              small
              color="primary"
              class="elevation-2"
              @click="fetchPayments()"
            >
              <font-awesome-icon icon="fa-solid fa-sync" />
            </v-btn>
          </v-col>
        </v-row>

        <!-- Statistics Cards -->
        <v-row class="ma-2">
          <v-col cols="12" md="4">
            <v-card color="blue-grey lighten-4" elevation="2" class="stat-card">
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption font-weight-medium blue-grey--text text--darken-3">Pending Payments</div>
                    <div class="text-h5 font-weight-bold blue-grey--text text--darken-4">{{ stats.pending }}</div>
                  </div>
                  <v-icon size="36" color="blue-grey darken-2">mdi-clock-outline</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card color="teal lighten-4" elevation="2" class="stat-card">
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption font-weight-medium teal--text text--darken-3">Validated Payments</div>
                    <div class="text-h5 font-weight-bold teal--text text--darken-4">{{ stats.validated }}</div>
                  </div>
                  <v-icon size="36" color="teal darken-2">mdi-check-circle</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card color="indigo lighten-4" elevation="2" class="stat-card">
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-caption font-weight-medium indigo--text text--darken-3">Total Amount (Pending)</div>
                    <div class="text-h6 font-weight-bold indigo--text text--darken-4">{{ formatAmount(stats.totalAmount) }}</div>
                  </div>
                  <v-icon size="36" color="indigo darken-2">mdi-cash-multiple</v-icon>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Table Header -->
        <v-card-title class="pt-4 pb-2">
          <div class="d-flex align-center">
            <v-icon color="primary" class="me-2">mdi-format-list-bulleted</v-icon>
            <span class="text-h6 font-weight-medium">Payment Requests</span>
            <v-chip class="ms-3" :color="getStatusFilterColor()" dark small>
              {{ statusFilter.toUpperCase() }}
            </v-chip>
          </div>
          <v-spacer></v-spacer>
          <span class="text-body-1 grey--text">({{ filteredPayments.length }}) records</span>
        </v-card-title>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="filteredPayments"
          item-key="id"
          class="table-rounded elevation-1"
          :items-per-page="15"
          disable-sort
          :loading="loading"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50],
            'items-per-page-text': 'Rows per page:'
          }"
        >
          <!-- Customer Info -->
          <template #[`item.user`]="{item}">
            <div class="d-flex flex-column" style="cursor: pointer">
              <span class="d-block font-weight-semibold text--primary text-body-2">{{ item.user.username }}</span>
              <small class="text-caption grey--text">{{ item.user.email }}</small>
            </div>
          </template>

          <!-- Order Info -->
          <template #[`item.order`]="{item}">
            <div class="d-flex flex-column">
              <span class="font-weight-semibold text-body-2">{{ item.order.order_id }}</span>
              <small class="text-caption grey--text">{{ item.order.shipping_location }}</small>
            </div>
          </template>

          <!-- Amount -->
          <template #[`item.amount`]="{item}">
            <span class="font-weight-bold primary--text text-body-2">{{ formatAmount(item.amount) }}</span>
          </template>

          <!-- Payment Info -->
          <template #[`item.payment_info`]="{item}">
            <div class="d-flex flex-column">
              <small class="text-caption"><strong>Ref:</strong> {{ item.payment_ref }}</small>
              <small class="text-caption"><strong>Phone:</strong> {{ item.phone_number }}</small>
              <v-chip x-small :color="getOperatorColor(item.payment_operator)" dark class="mt-1">
                {{ item.payment_operator }}
              </v-chip>
            </div>
          </template>

          <!-- Status with Click Action -->
          <template #[`item.confirmed`]="{item}">
            <v-chip
              small
              :color="statusColor[item.confirmed]"
              class="font-weight-medium"
              @click="openStatusChangeDialog(item)"
              style="cursor: pointer"
            >
              <v-icon left small>{{ item.confirmed ? 'mdi-check-circle' : 'mdi-clock-outline' }}</v-icon>
              <span v-if="item.confirmed">Validated</span>
              <span v-else>Pending</span>
            </v-chip>
          </template>

          <!-- Date -->
          <template #[`item.creation_date_str`]="{item}">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="text-caption">
                  {{ formatDateShort(item.creation_date) }}
                </span>
              </template>
              <span>{{ item.creation_date_str }}</span>
            </v-tooltip>
          </template>

          <!-- Actions -->
          <template #[`item.actions`]="{item}">
            <v-btn
              icon
              small
              color="primary"
              @click="viewDetails(item)"
            >
              <font-awesome-icon icon="fa-solid fa-eye" />
            </v-btn>
          </template>

          <!-- Empty State -->
          <template v-slot:no-data>
            <div class="text-center pa-5">
              <v-icon size="64" color="grey lighten-2">mdi-inbox-outline</v-icon>
              <div class="text-h6 grey--text mt-3">No payment requests found</div>
              <v-btn color="primary" class="mt-3" @click="fetchPayments" small>
                <font-awesome-icon icon="fa-solid fa-refresh" class="me-2" />
                Refresh
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-col>

    <!-- Status Change Dialog -->
    <v-dialog v-model="statusDialog" max-width="500">
      <v-card>
        <v-card-title
          class="white--text"
          :class="selectedPayment && selectedPayment.confirmed ? 'blue-grey' : 'teal'"
        >
          <v-icon left color="white">
            {{ selectedPayment && selectedPayment.confirmed ? 'mdi-close-circle' : 'mdi-check-circle' }}
          </v-icon>
          {{ selectedPayment && selectedPayment.confirmed ? 'Change to Pending?' : 'Validate Payment?' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <div v-if="selectedPayment">
            <p class="text-body-1 font-weight-medium">
              {{ selectedPayment.confirmed
                ? 'Are you sure you want to mark this payment as PENDING?'
                : 'Are you sure you want to VALIDATE this payment?'
              }}
            </p>

            <v-divider class="my-3"></v-divider>

            <div class="payment-summary pa-3">
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption">Customer:</td>
                    <td class="text-caption">{{ selectedPayment.user.username }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Order ID:</td>
                    <td class="text-caption">{{ selectedPayment.order.order_id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Amount:</td>
                    <td class="font-weight-bold primary--text text-caption">{{ formatAmount(selectedPayment.amount) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Payment Ref:</td>
                    <td class="text-caption">{{ selectedPayment.payment_ref }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <!-- Cancellation Reason (only if going from validated to pending) -->
            <div v-if="selectedPayment.confirmed" class="mt-4">
              <v-textarea
                v-model="cancelReason"
                label="Reason for changing to Pending *"
                placeholder="Please provide a reason for this status change..."
                outlined
                rows="3"
                :rules="[v => !!v || 'Reason is required']"
                counter
                dense
              ></v-textarea>

              <v-alert type="warning" dense class="mt-2">
                The customer will be notified of this change via email.
              </v-alert>
            </div>

            <v-alert v-else type="info" dense class="mt-3">
              This will mark the payment as validated and confirm the order.
            </v-alert>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="statusDialog = false" :disabled="processing">
            Cancel
          </v-btn>
          <v-btn
            :color="selectedPayment && selectedPayment.confirmed ? 'blue-grey' : 'teal'"
            dark
            @click="confirmStatusChange"
            :loading="processing"
            :disabled="selectedPayment && selectedPayment.confirmed && !cancelReason"
          >
            <v-icon left>
              {{ selectedPayment && selectedPayment.confirmed ? 'mdi-close' : 'mdi-check' }}
            </v-icon>
            {{ selectedPayment && selectedPayment.confirmed ? 'Change to Pending' : 'Validate Payment' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="700">
      <v-card v-if="selectedPayment">
        <v-card-title class="text-h5 white--text primary">
          <v-icon left color="white">mdi-information-outline</v-icon>
          Payment Details
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <h3 class="mb-3 text-subtitle-1 primary--text">Payment Information</h3>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption">Status:</td>
                    <td>
                      <v-chip :color="selectedPayment.confirmed ? 'teal' : 'blue-grey'" dark x-small>
                        {{ selectedPayment.confirmed ? 'VALIDATED' : 'PENDING' }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Amount:</td>
                    <td class="text-body-2 primary--text font-weight-bold">{{ formatAmount(selectedPayment.amount) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Payment Ref:</td>
                    <td class="text-caption">{{ selectedPayment.payment_ref }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Phone Number:</td>
                    <td class="text-caption">{{ selectedPayment.phone_number }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Operator:</td>
                    <td>
                      <v-chip x-small :color="getOperatorColor(selectedPayment.payment_operator)" dark>
                        {{ selectedPayment.payment_operator }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>

            <v-col cols="12" md="6">
              <h3 class="mb-3 text-subtitle-1 primary--text">Customer Information</h3>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption">Username:</td>
                    <td class="text-caption">{{ selectedPayment.user.username }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Email:</td>
                    <td class="text-caption">{{ selectedPayment.user.email }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>

            <v-col cols="12">
              <v-divider class="my-3"></v-divider>
              <h3 class="mb-3 text-subtitle-1 primary--text">Order Information</h3>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-caption">Order ID:</td>
                    <td class="text-caption">{{ selectedPayment.order.order_id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold text-caption">Shipping Location:</td>
                    <td class="text-caption">{{ selectedPayment.order.shipping_location }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from 'axios';
import Spinner from "@/components/Spinner.vue";

export default {
  name: 'PaymentValidation',

  components: {
    Spinner,
  },

  data() {
    const statusColor = {
      true: 'teal',
      false: 'blue-grey',
    };

    return {
      // Data
      payments: [],
      selectedPayment: null,

      // Filters
      search: '',
      statusFilter: 'pending',
      operatorFilter: 'all',
      statusOptions: [
        { text: 'All Payments', value: 'all' },
        { text: 'Pending Only', value: 'pending' },
        { text: 'Validated Only', value: 'confirmed' },
      ],
      operatorOptions: [
        { text: 'All Operators', value: 'all' },
        { text: 'MTN MoMo', value: 'mtn' },
        { text: 'Orange Money', value: 'orange' },


      ],

      // UI State
      loading: false,
      processing: false,
      statusDialog: false,
      detailsDialog: false,
      cancelReason: '',

      // Status colors
      statusColor,

      // Table headers
      headers: [
        { text: 'Customer', value: 'user' },
        { text: 'Order', value: 'order' },
        { text: 'Amount', value: 'amount' },
        { text: 'Payment Info', value: 'payment_info' },
        { text: 'Status', value: 'confirmed' },
        { text: 'Date/Time', value: 'creation_date_str' },
        { text: 'Actions', value: 'actions', align: 'center', width: '80' },
      ],

      // Statistics
      stats: {
        pending: 0,
        validated: 0,
        totalAmount: 0
      }
    };
  },

  computed: {filteredPayments() {
  let filtered = this.payments;

  // 🎯 Filtrer par opérateur
  if (this.operatorFilter) {
    if (this.operatorFilter === 'all') {
      // Si "All", ne garder que MTN et Orange
      filtered = filtered.filter(payment => {
        const operator = payment.payment_operator?.toLowerCase() || '';
        return operator.includes('mtn') || operator.includes('orange');
      });
    } else {
      // Sinon filtrer par l’opérateur sélectionné
      filtered = filtered.filter(payment => {
        const operator = payment.payment_operator?.toLowerCase() || '';
        return operator.includes(this.operatorFilter);
      });
    }
  }

  // 🔍 Filtrer par recherche
  if (this.search) {
    const searchLower = this.search.toLowerCase();
    filtered = filtered.filter(payment => {
      return (
        payment.payment_ref?.toLowerCase().includes(searchLower) ||
        payment.phone_number?.toLowerCase().includes(searchLower) ||
        payment.user?.username?.toLowerCase().includes(searchLower) ||
        payment.order?.order_id?.toLowerCase().includes(searchLower)
      );
    });
  }

  return filtered;
}

  },

  mounted() {
    this.fetchPayments();
    document.title = "Manager: Market Place | Payment Validation";
  },

  methods: {
    // Fetch payments from API
    async fetchPayments() {
      this.loading = true;

      await axios
        .get('/api/v1/market_place/manager/payments/pending/', {
          params: {
            status: this.statusFilter
          }
        })
        .then(response => {
          console.log(response.data);

          if (response.data.success) {
            this.payments = response.data.payments;
            this.calculateStats();
          } else {
            this.$store.commit('setSnackBarMessage', response.data.message || 'Failed to load payments');
            this.$store.commit('setSnackBarColor', 'error');
            this.$store.commit('activateSnackBar', true);
          }
        })
        .catch(error => {
          if (error.response) {
            this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText);
            this.$store.commit('setSnackBarColor', 'error');
            this.$store.commit('activateSnackBar', true);
          } else {
            this.$store.commit('setSnackBarMessage', 'An error occurred, please check your internet connection and try again later.');
            this.$store.commit('setSnackBarColor', 'error');
            this.$store.commit('activateSnackBar', true);
          }
        });

      this.loading = false;
    },

    // Calculate statistics
    calculateStats() {
      this.stats.pending = this.payments.filter(p => !p.confirmed).length;
      this.stats.validated = this.payments.filter(p => p.confirmed).length;
      this.stats.totalAmount = this.payments
        .filter(p => !p.confirmed)
        .reduce((sum, p) => sum + (p.amount || 0), 0);
    },

    // Open status change dialog
    openStatusChangeDialog(payment) {
      this.selectedPayment = payment;
      this.cancelReason = '';
      this.statusDialog = true;
    },

    // Confirm status change
// Confirm status change
async confirmStatusChange() {
  if (!this.selectedPayment) return;

  // If changing from validated to pending, require reason
  if (this.selectedPayment.confirmed && !this.cancelReason) {
    this.$store.commit('setSnackBarMessage', 'Please provide a reason for changing status');
    this.$store.commit('setSnackBarColor', 'warning');
    this.$store.commit('activateSnackBar', true);
    return;
  }

  this.processing = true;

  // Determine which endpoint to call
  const endpoint = this.selectedPayment.confirmed
    ? `/api/v1/market_place/manager/payments/${this.selectedPayment.id}/cancel/`
    : `/api/v1/market_place/manager/payments/${this.selectedPayment.id}/validate/`;

  let formData = new FormData();
  if (this.selectedPayment.confirmed && this.cancelReason) {
    formData.append('reason', this.cancelReason);
  }

  try {
    const response = await axios.post(endpoint, formData, {
      headers: {'Content-Type': 'multipart/form-data'}
    });

    console.log(response.data);

    if (response.data.success) {
      const message = this.selectedPayment.confirmed
        ? 'Payment status changed to pending'
        : 'Payment validated successfully!';

      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', 'success');
      this.$store.commit('activateSnackBar', true);
      this.$store.commit('setSnackBarTimeOut', 4000);

      this.statusDialog = false;
      this.selectedPayment = null;
      this.cancelReason = '';
      this.fetchPayments();
    } else {
      this.$store.commit('setSnackBarMessage', response.data.message || 'Operation failed');
      this.$store.commit('setSnackBarColor', 'error');
      this.$store.commit('activateSnackBar', true);
    }
  } catch (error) {
    console.error('Error in status change:', error);

    let errorMessage = 'An error occurred while processing your request.';

    if (error.response) {
      // Server responded with error status
      const serverMessage = error.response.data?.message;
      if (serverMessage) {
        errorMessage = serverMessage;
      } else {
        errorMessage = `${error.response.status} - ${error.response.statusText}`;
      }

      // Log detailed error for debugging
      console.error('Server error response:', error.response.data);
    } else if (error.request) {
      // Request made but no response received
      errorMessage = 'No response from server. Please check your connection.';
    }

    this.$store.commit('setSnackBarMessage', errorMessage);
    this.$store.commit('setSnackBarColor', 'error');
    this.$store.commit('activateSnackBar', true);
  }

  this.processing = false;
},
    // View payment details
    viewDetails(payment) {
      this.selectedPayment = payment;
      this.detailsDialog = true;
    },

    // Get status filter color
    getStatusFilterColor() {
      switch(this.statusFilter) {
        case 'pending': return 'blue-grey';
        case 'confirmed': return 'teal';
        default: return 'primary';
      }
    },

    // Format amount
    formatAmount(amount) {
      if (!amount) return '0 FCFA';
      return `${amount.toLocaleString()} FCFA`;
    },

    // Format date (short version)
    formatDateShort(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;

      return date.toLocaleDateString();
    },

    // Get operator color
getOperatorColor(operator) {
  // Si aucune valeur, traiter comme "My Account" => couleur par défaut 'blue'
  if (!operator) return 'blue';

  const op = operator.toLowerCase();

  if (op.includes('mtn') || op.includes('momo')) return 'deep-orange';
  if (op.includes('orange')) return 'orange darken-2';
  if (op.includes('account')) return 'blue'; // explicitement My Account
  return 'blue'; // couleur par défaut pour tout le reste
}


  }
};
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.stat-card {
  transition: transform 0.2s ease;
  border-radius: 10px;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.payment-summary {
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #1976d2;
}

.table-rounded {
  border-radius: 10px;
  overflow: hidden;
}

/* Make status chips more clickable */
.v-chip {
  transition: all 0.3s ease;
  min-width: 90px;
  justify-content: center;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

/* Improve table row hover effect */
::v-deep .v-data-table tbody tr:hover {
  background-color: #f5f7fa !important;
}

/* Better spacing for table cells */
::v-deep .v-data-table td {
  padding: 12px 16px !important;
}

::v-deep .v-data-table th {
  padding: 16px !important;
  font-weight: 600;
  background-color: #f8f9fa;
}
</style>

<style scoped>
.v-card {
  border-radius: 12px;
}

.stat-card {
  transition: transform 0.2s ease;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.payment-summary {
  background-color: #f5f7fa;
  border-radius: 8px;
  border-left: 4px solid #1976d2;
}

.table-rounded {
  border-radius: 10px;
  overflow: hidden;
}

/* Make status chips more clickable */
.v-chip {
  transition: all 0.3s ease;
  min-width: 90px;
  justify-content: center;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

/* Improve table row hover effect */
::v-deep .v-data-table tbody tr:hover {
  background-color: #e8f0fe !important;
}

/* Better spacing for table cells */
::v-deep .v-data-table td {
  padding: 12px 16px !important;
}

::v-deep .v-data-table th {
  padding: 16px !important;
  font-weight: 600;
  background-color: #f0f4f8;
  color: #37474f;
  font-size: 0.875rem;
}

/* Improve visibility of outlined inputs */
::v-deep .v-text-field--outlined fieldset,
::v-deep .v-select--outlined fieldset {
  border-color: rgba(0, 0, 0, 0.2) !important;
}

::v-deep .v-text-field--outlined:hover fieldset,
::v-deep .v-select--outlined:hover fieldset {
  border-color: rgba(0, 0, 0, 0.4) !important;
}

/* Improve dialog visibility */
::v-deep .v-dialog {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

/* Improve text contrast in alerts */
::v-deep .v-alert {
  border: 1px solid rgba(0, 0, 0, 0.1);
}
</style>
